const API_URL = process.env.REACT_APP_API_URL;
let settingsCache = null;
let settingsCacheAt = 0;
let settingsPromise = null;
const SETTINGS_CACHE_MS = 5 * 60 * 1000;

export const fetchSettings = async () => {
  try {
    if (settingsCache && Date.now() - settingsCacheAt < SETTINGS_CACHE_MS) {
      return settingsCache;
    }

    if (settingsPromise) {
      return await settingsPromise;
    }

    settingsPromise = (async () => {
    const response = await fetch(`${API_URL}/applicationsettings`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
      const data = await response.json();
      settingsCache = data;
      settingsCacheAt = Date.now();
      return data;
    })();

    return await settingsPromise;
  } catch (error) {
    console.error(`Error fetching settings`, error);
    throw error;
  } finally {
    settingsPromise = null;
  }
};

export const saveSettings = async (settings) => {
  try {
    // Create FormData object for multipart/form-data
    const formData = new FormData();
    
    // Add backend identification fields first (if they exist)
    if (settings.id) formData.append('Id', settings.id);
    if (settings.updatedDatetime) formData.append('UpdatedDatetime', settings.updatedDatetime);
    
    // Add all the basic fields
    if (settings.codeSociete) formData.append('CodeSociete', settings.codeSociete);
    if (settings.societe) formData.append('Societe', settings.societe);
    if (settings.matriculeFiscal) formData.append('MatriculeFiscal', settings.matriculeFiscal);
    if (settings.adresse) formData.append('Adresse', settings.adresse);
    if (settings.email) formData.append('Email', settings.email);
    if (settings.tel) formData.append('Tel', settings.tel);
    if (settings.fraisDeLivraison !== undefined) formData.append('FraisDeLivraison', String(settings.fraisDeLivraison).replace(',', '.'));
    if (settings.lienFacebook) formData.append('LienFacebook', settings.lienFacebook);
    if (settings.lienInstagram) formData.append('LienInstagram', settings.lienInstagram);
    if (settings.lienTiktok) formData.append('LienTiktok', settings.lienTiktok);
    if (settings.pixelBalise) formData.append('pixelBalise', settings.pixelBalise);
    if (settings.pixelId) formData.append('pixelId', settings.pixelId);
    if (settings.pixelToken) formData.append('pixelToken', settings.pixelToken);
    if (settings.coutLivraison !== undefined) formData.append('CoutLivraison', String(settings.coutLivraison).replace(',', '.'));
    if (settings.montantRetour !== undefined) formData.append('MontantRetour', String(settings.montantRetour).replace(',', '.'));
    if (settings.tauxLivraison !== undefined) formData.append('TauxLivraison', String(settings.tauxLivraison).replace(',', '.'));
    if (settings.title) formData.append('Title', settings.title);
    if (settings.description) formData.append('Description', settings.description);
    if (settings.descriptionLivraisonGratuit) formData.append('DescriptionLivraisonGratuit', settings.descriptionLivraisonGratuit);
    
    // Add branding fields
    if (settings.primaryColor) formData.append('primaryColor', settings.primaryColor);
    if (settings.secondaryColor) formData.append('secondaryColor', settings.secondaryColor);
    if (settings.storeTemplate) formData.append('storeTemplate', settings.storeTemplate);
    
    // Handle file uploads and URLs for images
    const imageFields = [
      'storeLogo', 'mainImage', 'leftFloatingImage', 'rightFloatingImage', 
      'bottomFloatingImage'
    ];
    
    // Handle login images with correct field names (lowercase as per backend response)
    const loginImageFields = [
      { frontend: 'loginImage1', backend: 'loginImage1' },
      { frontend: 'loginImage2', backend: 'loginImage2' },
      { frontend: 'loginImage3', backend: 'loginImage3' }
    ];
    
    // Process regular image fields - only send if changed
    imageFields.forEach(fieldName => {
      // Check if there's an uploaded file for this field (new file)
      if (settings.uploadedFiles && settings.uploadedFiles[fieldName]) {
        // Upload the actual file
        formData.append(fieldName, settings.uploadedFiles[fieldName]);
      } else if (settings[fieldName] && typeof settings[fieldName] === 'string' && 
                 !settings[fieldName].startsWith('blob:') && 
                 settings.originalValues && settings[fieldName] !== settings.originalValues[fieldName]) {
        // It's a URL string (not a blob) and it's different from original, send it
        formData.append(fieldName, settings[fieldName]);
      }
    });
    
    // Process login image fields with correct backend field names - only send if changed
    loginImageFields.forEach(({ frontend, backend }) => {
      // Check if there's an uploaded file for this field (new file)
      if (settings.uploadedFiles && settings.uploadedFiles[frontend]) {
        // Upload the actual file with backend field name
        formData.append(backend, settings.uploadedFiles[frontend]);
      } else if (settings[frontend] && typeof settings[frontend] === 'string' && 
                 !settings[frontend].startsWith('blob:') && 
                 settings.originalValues && settings[frontend] !== settings.originalValues[frontend]) {
        // It's a URL string (not a blob) and it's different from original, send it
        formData.append(backend, settings[frontend]);
      }
    });
    
    // Add login page text fields individually with correct backend field names for POST
    // Hero section text fields
    if (settings.heroTitle) formData.append('heroTitle', settings.heroTitle);
    if (settings.heroSubtitle) formData.append('heroSubtitle', settings.heroSubtitle);
    if (settings.heroDescription) formData.append('heroDescription', settings.heroDescription);
    if (settings.heroTag) formData.append('heroTag', settings.heroTag);
    if (settings.heroCollectionTitle) formData.append('heroCollectionTitle', settings.heroCollectionTitle);

    if (settings.title1) formData.append('title1', settings.title1);
    if (settings.description1) formData.append('description1', settings.description1);
    if (settings.accent1) formData.append('accent1', settings.accent1);
    if (settings.title2) formData.append('title2', settings.title2);
    if (settings.description2) formData.append('description2', settings.description2);
    if (settings.accent2) formData.append('accent2', settings.accent2);
    if (settings.title3) formData.append('title3', settings.title3);
    if (settings.description3) formData.append('description3', settings.description3);
    if (settings.accent3) formData.append('accent3', settings.accent3);

    const response = await fetch(`${API_URL}/applicationsettings/save`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        // Don't set Content-Type, let the browser set it with boundary for multipart/form-data
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    // Some backend responses return 200 with an empty or non-JSON body.
    // Treat that as success and fall back to the submitted payload.
    let data = null;
    const raw = await response.text();
    if (raw && raw.trim()) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
    }

    const normalizedData = data && typeof data === 'object' ? data : { ...settings };
    settingsCache = normalizedData;
    settingsCacheAt = Date.now();
    return normalizedData;
  } catch (error) {
    console.error(`Error saving settings`, error);
    throw error;
  }
};

